// Lazy loader for the Wall of Love themes.
//
// The manifests live in manifest.js because metadata is cheap and the editor
// wants all of it. The theme components live behind a dynamic import because a
// wall renders exactly one of them, and together they were 122 KB gzipped, of
// which framer-motion alone is 38 KB.
//
// The lower eight themes started life as the `category: "wall"` widgets and
// were forked into full-page versions: same designs, scaled up for a page
// takeover and paged in as the visitor scrolls. They are copies on purpose.
// The widget originals are embedded on other people's sites, so a wall can be
// reworked without touching a live embed.
//
// To add a theme: append its manifest to MANIFESTS in manifest.js, its file to
// LOADERS below, and its key to Wall::KINDS. All three are keyed by the same
// string, which is Wall#kind in the database.

import { useEffect, useState } from "react";

// The argument to import() has to be a literal path. Vite reads these
// statically to decide what to split into chunks, so a computed path would
// leave us with no chunks at all.
const LOADERS = {
  "cards":      () => import("./Cards"),
  "bubbles":    () => import("./Bubbles"),
  "chats":      () => import("./Chats"),
  "futuristic": () => import("./Futuristic"),
  "quotes":     () => import("./QuoteGrid"),
  "masonry":    () => import("./Masonry"),
  "tweets":     () => import("./TweetGrid"),
  "tabbed":     () => import("./TabbedWall"),
  "press":      () => import("./PressWall"),
  "gallery":    () => import("./ImageGallery"),
  "audio":      () => import("./AudioWall"),
  "reels":      () => import("./VideoReels"),
};

export const themeKinds = () => Object.keys(LOADERS);

// Loads the component for one theme. Every theme module default-exports its
// component. Returns { Theme, state }, where state is "loading", "ready",
// "unknown" (nothing registered under this kind) or "error" (the chunk failed
// to arrive). Callers have to treat "loading" as a real state: the wall page
// preloads its own theme chunk, but that is a head start, not a guarantee.
export const useWallTheme = (kind) => {
  const [Theme, setTheme] = useState(null);
  const [state, setState] = useState(() => (LOADERS[kind] ? "loading" : "unknown"));

  useEffect(() => {
    const loader = LOADERS[kind];
    if (!loader) {
      setTheme(null);
      setState("unknown");
      return undefined;
    }

    let cancelled = false;
    setState("loading");
    loader()
      .then((mod) => {
        if (cancelled) return;
        // setState treats a bare function as an updater, hence the wrapper.
        setTheme(() => mod.default);
        setState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setState("error");
      });

    return () => { cancelled = true; };
  }, [kind]);

  return { Theme, state };
};
